import React, { useState } from 'react';
import { Logo } from './Logo';
import { Button } from './Button';
import { Mail, MapPin, Twitter, Linkedin, Github } from 'lucide-react';
import { submitToGoogleScript } from '../../services/api';

export const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com/mindclave', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com/company/mindclave', label: 'LinkedIn' },
    { icon: Github, href: 'https://github.com/mindclave', label: 'GitHub' },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!email.trim()) {
      setSubmissionStatus({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setSubmissionStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmissionStatus({ type: null, message: '' });
    
    try {
      const response = await submitToGoogleScript('newsletter', { email });
      
      if (response.status === 'success') {
        setSubmissionStatus({ 
          type: 'success', 
          message: 'Thank you for subscribing to the MindClave newsletter!' 
        });
        setEmail('');
      } else {
        setSubmissionStatus({ 
          type: 'error', 
          message: response.message || 'Failed to subscribe. Please try again.' 
        });
      }
    } catch (error) {
      setSubmissionStatus({ 
        type: 'error', 
        message: 'Failed to subscribe. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#3D2914] text-white">
      <div className="px-4 md:px-16 py-16">
        {/* Top Section */}
        <div className="mb-12">
          {/* Brand Section */}
          <div>
            <Logo className="mb-4" dark={true} />
            <p className="text-gray-300 mb-6 max-w-sm">
              Making AI useful for everyone.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:hello@mindclave.com" className="hover:text-[#C97B5A] transition-colors">
                  hello@mindclave.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Cambridge, MA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-gray-600 pt-8 pb-8">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-gabarito font-bold text-2xl mb-2">Stay Updated</h3>
            <p className="text-gray-300 mb-6">
              Get the latest news and updates about MindClave.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-[#D4A574] transition-colors text-white placeholder-gray-400"
                disabled={isSubmitting}
              />
              <Button 
                variant="primary" 
                size="md" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            {submissionStatus.type && (
              <div className={`mt-4 text-sm ${
                submissionStatus.type === 'success' ? 'text-green-400' : 'text-red-400'
              }`}>
                {submissionStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-600 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-300">
            © {new Date().getFullYear()} MindClave. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  aria-label={social.label}
                >
                  <Icon className="w-5 h-5 text-gray-300 hover:text-[#C97B5A] transition-colors" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};